import { useCallback, useState } from 'react'
import * as Yup from 'yup'

import type { IVideoSubmit } from './VideoForm'
import type { ReactElement } from 'react'
import type ReactPlayer from 'react-player'
import type { IVideoCreate } from '~/types/video'

import VideoForm from './VideoForm'

import { useCreateVideoMutation, useUpdateVideoThumbnailMutation } from '../queries'

import { uploadVideoFile } from '~/api/file'
import Modal from '~/components/Modal'
import { ModalHeader } from '~/components/Modal/Layout'
import MwMLoader from '~/components/MwMLoader'
import { FORM_ERRORS } from '~/const'
import { useCreateFileMutation, useUploadFileMutation } from '~/queries/files'
import { displayToast } from '~/utils/toast'
import { YupOption } from '~/utils/yup'

import './styles.scss'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
}

const validationSchema: Yup.ObjectSchema<Yup.AnyObject> = Yup.object().shape({
  category: Yup.object({
    label: Yup.string(),
    value: Yup.string(),
  })
    .nullable()
    .required(FORM_ERRORS.REQUIRED),
  description: Yup.string(),
  file: Yup.string().nullable().required(FORM_ERRORS.REQUIRED),
  tags: Yup.array().of(YupOption),
  title: Yup.string().required(FORM_ERRORS.REQUIRED),
})

const CreateModal = ({ isOpen, onClose, onSubmit }: ModalProps): ReactElement => {
  const [videoPlayer, setVideoPlayer] = useState<ReactPlayer>()

  const [lockModal, setLockModal] = useState(false)

  const { mutateAsync: createVideoMutation } = useCreateVideoMutation()

  const { mutateAsync: updateVideoThumbnailMutation } = useUpdateVideoThumbnailMutation()

  const { mutateAsync: createFileMutation } = useCreateFileMutation()

  const { mutateAsync: uploadFileMutation } = useUploadFileMutation()

  const uploadThumbnailFile = useCallback(
    async (videoId: string) => {
      if (!videoPlayer) return

      const video = videoPlayer.getInternalPlayer()

      const canvas: HTMLCanvasElement = document.createElement('canvas') as HTMLCanvasElement
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight

      await canvas.getContext('2d')?.drawImage(video as CanvasImageSource, 0, 0, canvas.width, canvas.height)

      canvas.toBlob(async function (blob) {
        if (blob) {
          const file = new File([blob], 'thumbnail', { type: 'image/jpeg' })
          const { id: fileId, upload_url } = await createFileMutation({
            mimeType: file.type,
            name: file.name,
            size: file.size,
          })

          await uploadFileMutation({
            file: file,
            uploadUrl: upload_url,
          })

          updateVideoThumbnailMutation({ fileId, id: videoId })
        }
      })
    },
    [videoPlayer, createFileMutation, uploadFileMutation, updateVideoThumbnailMutation]
  )

  const handleSubmit = async (data: IVideoSubmit) => {
    setLockModal(true)
    try {
      const newVideo = await createVideoMutation(data.data as IVideoCreate)
      if (data.file) {
        await uploadVideoFile({ file: data.file, uploadUrl: newVideo.upload_url })
        uploadThumbnailFile(newVideo.id)
      }
      displayToast('Video successfully uploaded', 'success')
      onSubmit()
      onClose()
    } catch {
      displayToast('Video could not be uploaded', 'error')
    } finally {
      setLockModal(false)
    }
  }

  const handleVideoPreviewReady = (player: ReactPlayer) => {
    setVideoPlayer(player)
  }

  return (
    <Modal isOpen={isOpen} onCloseModal={onClose}>
      <ModalHeader>Add a new video</ModalHeader>
      <VideoForm
        onCancel={onClose}
        onSubmit={handleSubmit}
        onVideoPreviewReady={handleVideoPreviewReady}
        type="create"
        validationSchema={validationSchema}
      />
      {lockModal && <MwMLoader category="general" />}
    </Modal>
  )
}

export default CreateModal
